import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { UserRole, SCHOOL_BROWSE_ENABLED_FOR_STUDENTS } from '@tnf-recruit/shared'
import { useAuth } from './hooks/useAuth'

import { ProtectedRoute } from './components/ProtectedRoute'
import { Layout } from './components/Layout'
import { LandingPage } from './pages/LandingPage'
import { LoginPage } from './pages/LoginPage'
import { SignupPage } from './pages/SignupPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { DashboardPage } from './pages/DashboardPage'
import { ProfilePage } from './pages/ProfilePage'
import { ProfileEditPage } from './pages/ProfileEditPage'
import { BrowsePage } from './pages/BrowsePage'
import { StudentDetailPage } from './pages/StudentDetailPage'
import { SchoolBrowsePage } from './pages/SchoolBrowsePage'
import { SchoolDetailPage } from './pages/SchoolDetailPage'
import { SubscriptionPage } from './pages/SubscriptionPage'
import { SettingsPage } from './pages/SettingsPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage'

const schoolBrowseRoles = SCHOOL_BROWSE_ENABLED_FOR_STUDENTS
  ? [UserRole.STUDENT, UserRole.ADMIN]
  : [UserRole.ADMIN]

function App() {
  const { isLoading } = useAuth()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />

      {/* Protected routes */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Layout>
              <OnboardingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfileEditPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/browse"
        element={
          <ProtectedRoute allowedRoles={[UserRole.RECRUITER, UserRole.ADMIN]}>
            <Layout>
              <BrowsePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/:id"
        element={
          <ProtectedRoute allowedRoles={[UserRole.RECRUITER, UserRole.ADMIN]}>
            <Layout>
              <StudentDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/schools"
        element={
          <ProtectedRoute allowedRoles={schoolBrowseRoles}>
            <Layout>
              <SchoolBrowsePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/schools/:id"
        element={
          <ProtectedRoute allowedRoles={schoolBrowseRoles}>
            <Layout>
              <SchoolDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute allowedRoles={[UserRole.RECRUITER]}>
            <Layout>
              <SubscriptionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
